import { Module } from 'vuex';
import Vue from 'vue';
import { RootState } from '..';
import { ContactFormFields, ContactType } from '@/views/Contact/components/ContactForm/ContactForm.models';
import { getInitialFormData } from '@/views/Contact/Contact.data';
import { NotificationType } from '@/components/NotificationBanner/NotificationBanner.models';
import i18n from '../../i18n';
import router from '@/utils/router';
import { validate } from '@/utils/helpers/validation';
import { RouteNames } from '@/utils/router/router.models';
import { CustomerApi, AppointmentRequest } from '@/utils/api';
import { AxiosResponse } from 'axios';
import { tRoute } from '@/utils/plugins/i18nRoutes';
import { FormFieldData } from '@/components/FormField/formfield.models';

const SET_FIELD_VALUE = 'SET_FIELD_VALUE';
const SET_PRIVACY_VALUE = 'SET_PRIVACY_VALUE';
const SET_FIELD_REQUIRED = 'SET_FIELD_REQUIRED';
const SET_FIELD_ERROR = 'SET_FIELD_ERROR';
const CLEAR_FORM_ERRORS = 'CLEAR_FORM_ERRORS';
const RESET_STATE = 'RESET_STATE';

export interface ContactState {
  fields: ContactFormFields;
  privacyApproved: boolean;
}

const contactInitialState = (): ContactState => ({
  fields: getInitialFormData(),
  privacyApproved: false
});

const customerApi = new CustomerApi();
const vueTrackingInstance = new Vue();

export const contactModule: Module<ContactState, RootState> = {
  namespaced: true,
  state: contactInitialState(),
  getters: {},
  actions: {
    updateFieldValue({ commit }, data: { field: keyof ContactFormFields; value: string }) {
      commit(SET_FIELD_VALUE, data);
    },
    updatePrivacyValue({ commit }, value: boolean) {
      commit(SET_PRIVACY_VALUE, value);
    },
    async submitForm({ commit, state, dispatch, rootState }, type: ContactType) {
      let valid = true;

      const fields: [string, FormFieldData][] = Object.entries(state.fields).map(([name, data]) => {
        // read validators from initial data (they are constant) and place them here
        // when state is serialized, the non-serializable validators will get lost!
        const { validators } = getInitialFormData()[name];

        if (!validators) {
          throw new Error(`Cannot read validators for a field with name ${name}`);
        }

        return [
          name,
          {
            ...data,
            validators
          }
        ];
      });

      fields.forEach(([fieldName, fieldData]: [string, FormFieldData]) => {
        if (!validate(fieldData)) {
          valid = false;
          commit(SET_FIELD_ERROR, { field: fieldName, value: true });
        }
      });

      if (!rootState.configuration.houseConfiguration?.baufritzCode) {
        dispatch(
          'notify/showNotification',
          {
            headline: i18n.t('views.contact.notifications.codeMissing'),
            type: NotificationType.Error,
            origin: 'contact',
            key: 'contact/codeMissing'
          },
          { root: true }
        );
      }

      if (!state.privacyApproved) {
        dispatch(
          'notify/showNotification',
          {
            headline: i18n.t('views.contact.notifications.privacyMissing'),
            type: NotificationType.Error,
            origin: 'contact',
            key: 'contact/privacyMissing'
          },
          { root: true }
        );
      }

      if (!valid) {
        dispatch(
          'notify/showNotification',
          {
            headline: i18n.t('views.contact.notifications.validationError'),
            type: NotificationType.Error,
            origin: 'contact',
            key: 'contact/validationError'
          },
          { root: true }
        );
      }

      if (valid && state.privacyApproved && rootState.configuration.houseConfiguration?.baufritzCode) {
        const filteredFields = fields
          .filter(([, data]) => data.value)
          .map(([fieldName, fieldData]) => ({ [fieldName]: fieldData.value }));

        const appointmentRequestData: AppointmentRequest = Object.assign(
          {
            baufritzCode: rootState.configuration.houseConfiguration?.baufritzCode,
            appointmentType: type
          },
          ...filteredFields
        );

        try {
          await customerApi.postAppointment(i18n.locale, appointmentRequestData);
          vueTrackingInstance.$gtm.trackAppointment(type, appointmentRequestData['landAvailable']);

          dispatch(
            'notify/showNotification',
            {
              headline: i18n.t('views.contact.notifications.submitSuccess'),
              type: NotificationType.Success,
              closable: false,
              key: 'contact/submitSuccess'
            },
            { root: true }
          );
          router.push(tRoute(RouteNames.ContactConfirmation, {}, { type: type.toString() }));
          setTimeout(() => dispatch('closeSuccessNotification'), 4000);
        } catch (error) {
          const errorResponse: AxiosResponse = error.response;

          console.error(errorResponse);
        }
      }
    },
    clearFieldError({ commit }, field: keyof ContactFormFields) {
      commit(SET_FIELD_ERROR, { field, value: false });
    },
    closeValidationNotification({ dispatch }) {
      dispatch('notify/closeNotification', 'contact/validationError', { root: true });
    },
    closePrivacyNotification({ dispatch }) {
      dispatch('notify/closeNotification', 'contact/privacyMissing', { root: true });
    },
    closeSuccessNotification({ dispatch }) {
      dispatch('notify/closeNotification', 'contact/submitSuccess', { root: true });
    },
    resetState({ commit }) {
      commit(RESET_STATE);
    }
  },
  mutations: {
    [SET_FIELD_VALUE](state, { field, value }: { field: keyof ContactFormFields; value: string }) {
      state.fields[field].value = value;
    },
    [SET_PRIVACY_VALUE](state, value: boolean) {
      state.privacyApproved = value;
    },
    [SET_FIELD_REQUIRED](state, { field, value }: { field: keyof ContactFormFields; value: boolean }) {
      state.fields[field].required = value;
    },
    [SET_FIELD_ERROR](state, { field, value }: { field: keyof ContactFormFields; value: boolean }) {
      state.fields[field].hasError = value;
    },
    [CLEAR_FORM_ERRORS](state) {
      Object.values(state.fields).forEach(field => (field.hasError = false));
    },
    [RESET_STATE](state) {
      Object.assign(state, contactInitialState());
    }
  }
};
